<section class="card mb-3" aria-labelledby="license-usage-heading">
  <div class="card-body">
    <div
      class="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-3"
    >
      <div>
        <h5 id="license-usage-heading" class="card-title mb-1">
          {{ 'organizations.licenseUsage.title' | translate }}
        </h5>
        <p class="text-muted mb-0">
          {{ 'organizations.licenseUsage.subtitle' | translate }}
        </p>
      </div>
      <div class="d-flex flex-wrap align-items-end gap-2">
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
          (click)="configureEntitlements.emit()"
          [attr.title]="'organizations.licenseEntitlements.action' | translate"
          [attr.aria-label]="
            'organizations.licenseEntitlements.action' | translate
          "
        >
          <span class="material-symbols-outlined">settings</span>
          <span>{{
            'organizations.licenseEntitlements.action' | translate
          }}</span>
        </button>
        <div class="d-flex flex-column">
          <label class="form-label d-block mb-1" for="license-usage-month">
            {{ 'organizations.licenseUsage.month' | translate }}
          </label>
          <p-datepicker
            inputId="license-usage-month"
            [(ngModel)]="monthPickerValue"
            view="month"
            dateFormat="mm/yy"
            [maxDate]="maxMonth"
            [showIcon]="true"
            [showButtonBar]="false"
            [showClear]="false"
            [readonlyInput]="true"
            [invalid]="monthError"
            (onSelect)="onMonthSelect($event)"
          >
            <ng-template #footer>
              @if (availableMonths.length > 0) {
                <div class="border-top px-3 py-2">
                  <small class="text-muted d-block mb-1">
                    {{
                      'organizations.licenseUsage.availableMonths' | translate
                    }}
                  </small>
                  <div class="d-flex flex-wrap gap-1">
                    @for (month of availableMonthLabels(); track month) {
                      <span class="badge text-bg-secondary">{{ month }}</span>
                    }
                  </div>
                </div>
              }
            </ng-template>
          </p-datepicker>
          @if (monthError) {
            <div class="invalid-feedback d-block">
              {{ 'organizations.licenseUsage.invalidMonth' | translate }}
            </div>
          }
        </div>
        <div class="license-usage-types">
          <label class="form-label mb-1" for="license-usage-types">
            {{ 'organizations.licenseUsage.typesLabel' | translate }}
          </label>
          <p-multiselect
            inputId="license-usage-types"
            [(ngModel)]="selectedTypes"
            [options]="availableTypes"
            optionLabel="label"
            optionValue="value"
            [showToggleAll]="true"
            [fluid]="true"
            (onChange)="onTypesChange()"
          />
        </div>
      </div>
    </div>

    @if (loading) {
      <div class="text-muted">{{ 'loading' | translate }}</div>
    }

    @if (!loading && failedSources.length > 0) {
      <div class="alert alert-warning" role="alert">
        {{
          'organizations.licenseUsage.partialFailure'
            | translate: { sources: failedSources.join(', ') }
        }}
      </div>
    }

    @if (!loading && reports.length === 0 && failedSources.length === 0) {
      <p class="text-muted mb-0">
        {{ 'organizations.licenseUsage.empty' | translate }}
      </p>
    }

    @for (report of reports; track report.source + report.type) {
      <article class="border rounded p-3 mb-3">
        <div class="d-flex flex-wrap justify-content-between gap-2 mb-3">
          <h6 class="mb-0">{{ typeLabel(report.type) }}</h6>
          <span class="badge text-bg-secondary">{{ report.source }}</span>
        </div>

        @if (report.availability === 'NO_DATA') {
          <p class="text-muted mb-0">
            {{ 'organizations.licenseUsage.noData' | translate }}
          </p>
        } @else {
          <div class="row g-3 mb-3">
            <div class="col-12 col-md-4">
              <small class="text-muted text-uppercase">{{
                'organizations.licenseUsage.opening' | translate
              }}</small>
              <div class="fs-4 fw-semibold">
                {{ report.openingUsage ?? '-' }}
              </div>
            </div>
            <div class="col-12 col-md-4">
              <small class="text-muted text-uppercase">{{
                'organizations.licenseUsage.peak' | translate
              }}</small>
              <div class="fs-4 fw-semibold">{{ report.peakUsage ?? '-' }}</div>
              @if (report.peakObservedAt) {
                <small class="text-muted">{{
                  report.peakObservedAt | dateTime
                }}</small>
              }
            </div>
            <div class="col-12 col-md-4">
              <small class="text-muted text-uppercase">{{
                'organizations.licenseUsage.closing' | translate
              }}</small>
              <div class="fs-4 fw-semibold">
                {{ report.closingUsage ?? '-' }}
              </div>
              @if (report.asOf) {
                <small class="text-muted">{{ report.asOf | dateTime }}</small>
              }
            </div>
          </div>

          @if (report.observations.length > 0) {
            <div class="table-responsive">
              <table class="table table-sm mb-0">
                <thead>
                  <tr>
                    <th scope="col">
                      {{ 'organizations.licenseUsage.observedAt' | translate }}
                    </th>
                    <th scope="col">
                      {{ 'organizations.licenseUsage.usedCount' | translate }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  @for (
                    observation of report.observations;
                    track observation.observedAt
                  ) {
                    <tr>
                      <td>{{ observation.observedAt | dateTime }}</td>
                      <td>{{ observation.usedCount }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        }
      </article>
    }
  </div>
</section>
